/**
 * WHAT A PAGE ASKS FOR, STARTED BY ITS ROUTE (målt på testmiljøet 3. oktober).
 *
 * En full innlasting av «Personer og ressurser» var fire etapper etter `/v1/me/bootstrap`: sidens
 * kode, så `GET /v1/editions/:id` (~190 ms), så de ni kallene fanen faktisk trenger, og til slutt
 * kolonnevalget — fordi tabellen som spør om det først tegnes når lista har kommet. Hver etappe
 * ventet på den forrige uten å trenge svaret: nøklene står i adressen.
 *
 * Rutenes `loader` kaller funksjonene her, og de starter de samme spørringene — med de samme
 * `*QueryOptions` og dermed de samme nøklene — mens sidekoden lastes. Komponenten finner dem i
 * cachen (eller underveis) og spør ikke igjen.
 *
 * TRE REGLER, OG HVER AV DEM ER EN FEIL SOM ER LETT Å GJØRE:
 *
 * 1. INGENTING HER ER `await`. En loader som ventet ville holdt ruta i `pending` og byttet sidens
 *    egne skjeletter mot ruterens — og gjort den tregeste spørringen til hele sidens tid.
 * 2. BARE DET SIDEN SELV VILLE SPURT OM, MED DEN SAMME PORTEN. En spørring komponenten har
 *    `enabled: allows(...)` på, startes her under det samme vilkåret. Ellers blir en celle satt til
 *    nei en 403 i bakgrunnen — avgjørelse 0005 lest på en forespørsel sier at den ikke skal sendes.
 * 3. INGENTING SOM TRENGER ET ANNET SVAR. Ressursenes tildelinger og konflikter spør om et vindu
 *    regnet av årgangens startdato og sone; den finnes ikke før årgangen har svart, så de blir
 *    stående hos komponenten.
 *
 * Og rutene kaller dem bare når `cause === 'enter'`. `preload` er en mus som hviler over en lenke
 * i 50 ms (`defaultPreload: 'intent'`) — ti kall per sveip over sidemenyen er en pris ingen har
 * bestemt å betale. `stay` er et fanebytte på samme side: årgangen ligger alt i cachen, og fanen som
 * monteres spør selv i samme øyeblikk.
 *
 * `route-prefetch.test.tsx` tegner hver side og holder hver nøkkel herfra opp mot det siden faktisk
 * hentet. En regel som drifter her blir en rød test, ikke et stille dobbeltkall.
 */
import type { MeResponse } from '@tvitvi/shared';
import type { QueryClient } from '@tanstack/react-query';

import { contactsQueryOptions } from '@/api/queries/contacts';
import { editionQueryOptions } from '@/api/queries/editions';
import { festivalQueryOptions, festivalsQueryOptions } from '@/api/queries/festivals';
import { stagesQueryOptions } from '@/api/queries/stages';
import { tablePrefsQueryOptions } from '@/api/queries/table-prefs';
import { viewPrefsQueryOptions, viewKeyFor } from '@/api/queries/view-prefs';
import { actorFromMe, allows } from '@/auth/actor';
import { organizationKindOf } from '@/auth/organization-kind';
import { declaredModulesOf } from '@/auth/organization-modules';
import { monthBounds } from '@/components/calendar/MonthGrid';
import { accreditationLevelsQueryOptions } from '@/features/accreditation/accreditation-queries';
import { enquiriesQueryOptions, enquiryQueryOptions } from '@/features/agency/agency-queries';
import { registerQueryOptions } from '@/features/booking-register/booking-register-queries';
import { calendarItemsQueryOptions } from '@/features/calendar/items-queries';
import {
  firstWeekdayOf,
  startOfMonth,
  toDateValue,
  windowFor,
} from '@/features/calendar/month-grid';
import { calendarQueryOptions } from '@/features/calendar/queries';
import { validateCalendarSearch } from '@/features/calendar/search-params';
import { anchorOf, windowForView } from '@/features/calendar/view-window';
import { controlRoomSmsQueryOptions } from '@/features/control-room/messaging/sms-queries';
import { controlRoomOverviewQueryOptions } from '@/features/control-room/organizations/organization-queries';
import {
  venueEnquiriesQueryOptions,
  venueEnquiryMessagesQueryOptions,
  venueEnquiryQueryOptions,
  venueEnquirySummaryQueryOptions,
} from '@/features/date-requests/date-request-queries';
import { validateVenueRequestsSearch } from '@/features/date-requests/venue-requests-search';
import { editionDepartmentsQueryOptions } from '@/features/festivals/edition-departments';
import {
  participationRolesQueryOptions,
  participationSummaryQueryOptions,
  participationsQueryOptions,
  personFieldsQueryOptions,
} from '@/features/people/participation-queries';
import {
  editionAccessCandidatesQueryOptions,
  shiftsQueryOptions,
  staffingRolesQueryOptions,
} from '@/features/people/people-queries';
import { peopleTabAccess, resolvePeopleTab } from '@/features/people/people-tabs';
import { performancesQueryOptions } from '@/features/performances/queries';
import {
  performanceListFilters,
  searchOf,
  sortOf,
  stageOf,
  statusesOf,
  validatePerformancesSearch,
} from '@/features/performances/search-params';
import {
  resourceDepartmentSummaryQueryOptions,
  resourceTypesQueryOptions,
  resourcesQueryOptions,
} from '@/features/resources/resource-queries';
import { RESOURCES_VIEWS, type ResourcesView } from '@/features/resources/resources-views';
import { linkPeopleQueryOptions } from '@/features/share-link/share-link-queries';
import {
  summaryFoodQueryOptions,
  summaryLogisticsQueryOptions,
  summaryStayQueryOptions,
} from '@/features/summary/summary-queries';
import { resolveSummaryTab, visibleSummaryTabs } from '@/features/summary/summary-tabs';
import { departmentsQueryOptions } from '@/features/tasks/queries';
import { taskUnassignableQueryOptions } from '@/features/tasks/assignable';
import { periodOf, validateEventsSearch } from '@/features/venue/events-search';
import { validateRoomsSearch } from '@/features/venue/rooms-search';
import {
  NIGHTS_MONTH_PAGE_SIZE,
  venueNightsQueryOptions,
  venueRentPackagesQueryOptions,
  venueRoomsQueryOptions,
} from '@/features/venue/venue-queries';
import { venueBaseResourcesQueryOptions } from '@/features/venue/venue-resources-queries';
import { validateVenueCrewSearch } from '@/features/venue-crew/search-params';
import {
  venueCrewPlanQueryOptions,
  venueCrewQueryOptions,
} from '@/features/venue-crew/venue-crew-queries';
import { addDays, mondayOf, todayIso } from '@/features/venue-crew/week';
import { todayIn } from '@/features/people/shift-window';

export interface EditionRouteParams {
  festivalId: string;
  editionId: string;
}

/** The address bar's search, as the router parsed it — NOT yet validated by the route. */
export type RawSearch = Record<string, unknown>;

/**
 * «Personer og ressurser» — `/…/people`, og `/…/resources`, som videresender hit.
 *
 * Fanen avgjøres av den samme `resolvePeopleTab` siden bruker, og hver fane henter bare sitt:
 * Radix monterer bare den valgte fanen, og en fane ingen ser skal ikke koste en forespørsel.
 */
export function prefetchEditionPeople(
  queryClient: QueryClient,
  me: MeResponse,
  params: EditionRouteParams,
  search: RawSearch,
): void {
  const organizationId = me.activeOrganizationId;
  const { festivalId, editionId } = params;
  if (organizationId === null || editionId === '') return;

  const actor = actorFromMe(me);
  const tab = resolvePeopleTab(search['tab'], peopleTabAccess(actor));
  const mayReadEdition = allows(actor, 'edition:read');

  /*
   * ÅRGANGEN — det hver fane venter på. `PeoplePage` spør bare med `edition:read`, men
   * `ResourcesPage` spør uten port, så på ressursfanen hentes den uansett.
   */
  if (mayReadEdition || tab === 'resources') {
    void queryClient.prefetchQuery(editionQueryOptions(organizationId, editionId));
  }

  /* Stab og vakter og Personer tegnes først når årgangen er der, og den hentes bare med cella. */
  if (tab === 'staffing' && mayReadEdition) {
    void queryClient.prefetchQuery(staffingRolesQueryOptions(organizationId, editionId));
    /* «Finnes det vakter i året?» Uka som står oppe trenger årgangens dato og venter. */
    void queryClient.prefetchQuery(shiftsQueryOptions(organizationId, editionId, false));
    /* Fanen spør med `edition.festivalId`; adressen bærer den samme. */
    void queryClient.prefetchQuery(resourceTypesQueryOptions(organizationId, festivalId));
    void queryClient.prefetchQuery(editionDepartmentsQueryOptions(organizationId, editionId));
    void queryClient.prefetchQuery(participationSummaryQueryOptions(organizationId, editionId));
  }

  if (tab === 'participants' && mayReadEdition) {
    void queryClient.prefetchQuery(editionDepartmentsQueryOptions(organizationId, editionId));
    void queryClient.prefetchQuery(participationRolesQueryOptions(organizationId));
    void queryClient.prefetchQuery(participationSummaryQueryOptions(organizationId, editionId));
    void queryClient.prefetchQuery(accreditationLevelsQueryOptions(organizationId, editionId));
    if (allows(actor, 'person_field:list')) {
      void queryClient.prefetchQuery(personFieldsQueryOptions(organizationId, festivalId));
    }
    /* Lista uten filtre — det fana åpner på. */
    void queryClient.prefetchQuery(
      participationsQueryOptions(organizationId, editionId, {
        departmentId: undefined,
        q: undefined,
      }),
    );
    void queryClient.prefetchQuery(linkPeopleQueryOptions(organizationId, editionId));
    if (allows(actor, 'festival:manage_roles')) {
      void queryClient.prefetchQuery(
        editionAccessCandidatesQueryOptions(organizationId, editionId),
      );
    }
    void queryClient.prefetchQuery(tablePrefsQueryOptions('edition-participants'));
  }

  if (tab === 'resources') {
    /* Rutas `validateSearch` slipper bare `view` gjennom på ressursfanen, og bare en kjent verdi. */
    const rawView = search['view'];
    const view: ResourcesView =
      typeof rawView === 'string' && (RESOURCES_VIEWS as readonly string[]).includes(rawView)
        ? (rawView as ResourcesView)
        : 'list';

    /*
     * TO TYPELISTER, OG DET ER IKKE ET DOBBELTKALL: den ene uten de arkiverte (filteret og
     * gruppene), den andre med (typen bak en rad hvis typen er arkivert). Ulike svar, ulike nøkler —
     * se `resourceTypesQueryOptions`. Panelet og flytt-dialogen leser den første fra cachen.
     */
    void queryClient.prefetchQuery(resourceTypesQueryOptions(organizationId, festivalId));
    void queryClient.prefetchQuery(resourceTypesQueryOptions(organizationId, festivalId, true));
    void queryClient.prefetchQuery(editionDepartmentsQueryOptions(organizationId, editionId));
    void queryClient.prefetchQuery(
      resourceDepartmentSummaryQueryOptions(organizationId, editionId),
    );
    if (allows(actor, 'participation:list')) {
      void queryClient.prefetchQuery(participationSummaryQueryOptions(organizationId, editionId));
    }
    void queryClient.prefetchQuery(
      resourcesQueryOptions(organizationId, editionId, {
        departmentId: undefined,
        resourceTypeId: undefined,
        q: undefined,
      }),
    );
    if (view === 'list') {
      void queryClient.prefetchQuery(tablePrefsQueryOptions('edition-resources'));
      /*
       * Ansvarlig-velgeren i lista. Siden spør også når en `limited` eier en avdeling, men det
       * avgjøres av avdelingstreet — et svar — så her bare for den som kan endre alle.
       */
      if (allows(actor, 'resource:update')) {
        void queryClient.prefetchQuery(participationsQueryOptions(organizationId, editionId, {}));
      }
    }
  }
}

/** Lineup — `/…/performances`. Ingenting her venter på årgangen; alt venter på sidekoden. */
export function prefetchEditionPerformances(
  queryClient: QueryClient,
  me: MeResponse,
  params: EditionRouteParams,
  search: RawSearch,
): void {
  const organizationId = me.activeOrganizationId;
  const { festivalId, editionId } = params;
  if (organizationId === null || editionId === '') return;

  const actor = actorFromMe(me);
  const validated = validatePerformancesSearch(search);
  void queryClient.prefetchQuery(
    performancesQueryOptions(
      organizationId,
      editionId,
      performanceListFilters(
        statusesOf(validated),
        stageOf(validated),
        searchOf(validated),
        sortOf(validated),
      ),
    ),
  );
  if (allows(actor, 'stage:list') && festivalId !== '') {
    void queryClient.prefetchQuery(stagesQueryOptions(organizationId, festivalId, editionId));
  }
  if (allows(actor, 'edition:read')) {
    void queryClient.prefetchQuery(editionQueryOptions(organizationId, editionId));
  }
  void queryClient.prefetchQuery(tablePrefsQueryOptions('lineup'));
}

/**
 * Årgangens kalender — `/…/calendar`.
 *
 * Vinduet regnes med de samme funksjonene `CalendarWorkspace` bruker, fra adressen, dagen i dag
 * og leserens locale. Et lagret visningsvalg kan flytte siden til andre filtre når det kommer;
 * det gjør den i dag også, og nøkkelen herfra er den siden spør om før det.
 */
export function prefetchEditionCalendar(
  queryClient: QueryClient,
  me: MeResponse,
  params: EditionRouteParams,
  search: RawSearch,
  today: Date = new Date(),
): void {
  const organizationId = me.activeOrganizationId;
  const { festivalId, editionId } = params;
  if (organizationId === null || festivalId === '' || editionId === '') return;

  const actor = actorFromMe(me);
  const personalHouse = organizationKindOf(me) === 'personal';
  const validated = validateCalendarSearch(search);
  const view = validated.view ?? 'month';
  const anchor = anchorOf(validated.date, validated.month, today);
  const firstWeekday = firstWeekdayOf(me.user.locale);
  const window = windowForView(view, anchor, firstWeekday);

  if (allows(actor, 'edition:read')) {
    void queryClient.prefetchQuery(editionQueryOptions(organizationId, editionId));
  }
  void queryClient.prefetchQuery(
    calendarItemsQueryOptions(organizationId, {
      from: window.from,
      to: window.to,
      editionId,
      kinds: validated.kinds,
      departmentId: personalHouse ? undefined : validated.departmentId,
      personId: personalHouse ? undefined : validated.personId,
      mine: validated.mine,
      q: validated.q,
    }),
  );
  if (view === 'month') {
    const monthDays = windowFor(startOfMonth(anchor), firstWeekday);
    void queryClient.prefetchQuery(
      calendarQueryOptions(organizationId, {
        from: toDateValue(monthDays[0] ?? startOfMonth(anchor)),
        to: toDateValue(monthDays[monthDays.length - 1] ?? startOfMonth(anchor)),
        editionId,
      }),
    );
  }
  if (!personalHouse && allows(actor, 'department:list')) {
    void queryClient.prefetchQuery(departmentsQueryOptions(organizationId));
  }
  if (!personalHouse && allows(actor, 'contact:list')) {
    void queryClient.prefetchQuery(
      contactsQueryOptions(organizationId, { sort: 'name', direction: 'asc' }),
    );
    if (allows(actor, 'task:list')) {
      void queryClient.prefetchQuery(taskUnassignableQueryOptions(organizationId));
    }
  }
  void queryClient.prefetchQuery(viewPrefsQueryOptions(viewKeyFor('calendar', organizationId)));
}

/**
 * Oppsummering — `/…/summary`. Logistikk, overnatting og mat; kartet og økonomien hentes av
 * komponentene sine (kartet laster Leaflet, økonomien er sin egen side i siden).
 *
 * Kolonnevalget startes sammen med lista. Tabellen tegnes først når lista har dager, så for en tom
 * årgang er det ett lite kall siden ellers ikke ville gjort — mot en hel etappe for alle andre.
 */
export function prefetchEditionSummary(
  queryClient: QueryClient,
  me: MeResponse,
  params: EditionRouteParams,
  search: RawSearch,
): void {
  const organizationId = me.activeOrganizationId;
  const { editionId } = params;
  if (organizationId === null || editionId === '') return;

  const actor = actorFromMe(me);
  if (allows(actor, 'edition:read')) {
    void queryClient.prefetchQuery(editionQueryOptions(organizationId, editionId));
  }
  const tab = resolveSummaryTab(search['tab'], visibleSummaryTabs(actor));
  if (tab === 'logistics') {
    void queryClient.prefetchQuery(summaryLogisticsQueryOptions(organizationId, editionId));
    void queryClient.prefetchQuery(tablePrefsQueryOptions('summary-travel'));
  } else if (tab === 'stay') {
    void queryClient.prefetchQuery(summaryStayQueryOptions(organizationId, editionId));
    void queryClient.prefetchQuery(tablePrefsQueryOptions('summary-stay'));
  } else if (tab === 'food') {
    void queryClient.prefetchQuery(summaryFoodQueryOptions(organizationId, editionId));
    void queryClient.prefetchQuery(tablePrefsQueryOptions('summary-meals'));
  }
}

/**
 * Byråets Artister — `/artists`.
 *
 * Fanen er den siden lander på FØR en `?enquiry=` er slått opp: den kan flytte siden til
 * forespørslene når svaret kommer, og det avgjøres av svaret. Oppslaget selv startes her.
 */
export function prefetchAgencyArtists(
  queryClient: QueryClient,
  me: MeResponse,
  search: RawSearch,
): void {
  const organizationId = me.activeOrganizationId;
  if (organizationId === null) return;

  const enquiry = search['enquiry'];
  if (typeof enquiry === 'string') {
    void queryClient.prefetchQuery(enquiryQueryOptions(organizationId, enquiry));
  }
  if (search['tab'] === 'enquiries') {
    /*
     * Sidas standardfilter skjuler «Falt bort» (`hideFellThrough`, 253.70) — det er nøkkelen den
     * spør med. Med `{}` her hentet forhåndshentingen en liste sida aldri leste (C-ovr målte det).
     */
    void queryClient.prefetchQuery(
      enquiriesQueryOptions(organizationId, { hideFellThrough: true }),
    );
    void queryClient.prefetchQuery(tablePrefsQueryOptions('agency-enquiries'));
  } else {
    void queryClient.prefetchQuery(registerQueryOptions(organizationId, {}));
    void queryClient.prefetchQuery(tablePrefsQueryOptions('agency-artists'));
  }
}

/**
 * SKALLET — det sidemenyen og `useRememberLastEdition` spør om, på hver side.
 *
 * Skallet tegnes først når sidens kode er lastet (ruteren venter på hver `lazyRouteComponent` i
 * treffet), så visningsvalget og spillestedenes tellere startet først da — en etappe etter
 * `/v1/me/bootstrap`, målt på Spillested › Personer 4. oktober. Festivallista ligger i cachen fra
 * bootstrap når loaderen kjører; mangler den, spør menyen selv som før.
 */
export function prefetchShell(queryClient: QueryClient, me: MeResponse): void {
  const organizationId = me.activeOrganizationId;
  if (organizationId === null) return;

  void queryClient.prefetchQuery(viewPrefsQueryOptions(viewKeyFor('festival', organizationId)));

  /* `Sidebar`, `VenueNavSection` og `VenueNavItem`, port for port. */
  const actor = actorFromMe(me);
  if (actor?.orgRole === 'guest' || !allows(actor, 'festival:list')) return;
  /* QA16 funn 28: merket leser tellingen fanen leser (`/venue-enquiries/summary`), ikke Oversikt. */
  if (!allows(actor, 'venue_enquiry:list')) return;
  const declared = declaredModulesOf(me);
  const venues = (
    queryClient.getQueryData(festivalsQueryOptions(organizationId).queryKey)?.items ?? []
  ).filter((festival) => festival.kind === 'venue');
  /* Uten `modules` i `/v1/me` (overgangen) utleder `useHouseModules` Spillested slik. */
  const hasVenueModule =
    declared === undefined ? organizationKindOf(me) === 'festival' : declared.includes('venue');
  if (!hasVenueModule) return;
  for (const venue of venues) {
    void queryClient.prefetchQuery(
      venueEnquirySummaryQueryOptions(organizationId, { festivalId: venue.id }),
    );
  }
}

export interface VenueRouteParams {
  festivalId: string;
}

/**
 * Spillested › Personer — `/venue/$festivalId/people`.
 *
 * `VenueGate` slår opp huset FØR siden tegnes, og mannskapet ble først spurt om etter det: sidens
 * kode, så huset, så lista. Alle tre nøklene står i adressen.
 */
export function prefetchVenuePeople(
  queryClient: QueryClient,
  me: MeResponse,
  params: VenueRouteParams,
  search: RawSearch,
  today: Date = new Date(),
): void {
  const organizationId = me.activeOrganizationId;
  const { festivalId } = params;
  if (organizationId === null || festivalId === '') return;
  /* Rutas egen dør: `refuseGuest` og `crew_member:list`. */
  if (me.activeOrgRole === 'guest' || !allows(actorFromMe(me), 'crew_member:list')) return;

  void queryClient.prefetchQuery(festivalQueryOptions(organizationId, festivalId));
  /* Radix monterer bare den valgte fanen. Vaktplanen åpner på denne uka, uten filteret. */
  if (validateVenueCrewSearch(search).tab === 'plan') {
    const week = mondayOf(todayIso(today));
    void queryClient.prefetchQuery(
      venueCrewPlanQueryOptions(organizationId, festivalId, {
        from: week,
        to: addDays(week, 6),
        onlyGaps: false,
      }),
    );
  } else {
    void queryClient.prefetchQuery(venueCrewQueryOptions(organizationId, festivalId));
  }
}

/**
 * Spillested › Arrangementer — `/venue/$festivalId/events`.
 *
 * «Kommende» er fra og med i dag i HUSETS sone, og siden leser sonen fra huset. Festivallista fra
 * bootstrap bærer den samme sonen, så nøkkelen regnes her uten å vente på huset.
 */
export function prefetchVenueEvents(
  queryClient: QueryClient,
  me: MeResponse,
  params: VenueRouteParams,
  search: RawSearch,
  now: Date = new Date(),
  /** Under Saler (QA15-S B3): salen fra stien, eller `null` for alle saler. */
  room?: string | null,
): void {
  const organizationId = me.activeOrganizationId;
  const { festivalId } = params;
  if (organizationId === null || festivalId === '') return;
  if (!allows(actorFromMe(me), 'venue_night:list')) return;

  void queryClient.prefetchQuery(festivalQueryOptions(organizationId, festivalId));
  void queryClient.prefetchQuery(venueRoomsQueryOptions(organizationId, festivalId));

  const house =
    queryClient.getQueryData(festivalQueryOptions(organizationId, festivalId).queryKey) ??
    queryClient
      .getQueryData(festivalsQueryOptions(organizationId).queryKey)
      ?.items.find((festival) => festival.id === festivalId);
  const validated = validateEventsSearch(search);
  const today = todayIn(house?.timezone ?? 'Europe/Oslo', now);
  const roomId = room === undefined ? validated.roomId : (room ?? undefined);
  const shared = { roomId, status: validated.status, q: validated.q };
  if ((validated.view ?? 'list') === 'list') {
    void queryClient.prefetchInfiniteQuery(
      venueNightsQueryOptions(organizationId, festivalId, {
        ...periodOf(validated, today),
        ...shared,
      }),
    );
    void queryClient.prefetchQuery(tablePrefsQueryOptions('venue-nights'));
  } else {
    const bounds = monthBounds(validated.month ?? today.slice(0, 7));
    void queryClient.prefetchInfiniteQuery(
      venueNightsQueryOptions(
        organizationId,
        festivalId,
        { from: bounds.from, to: bounds.to, direction: 'asc', ...shared },
        NIGHTS_MONTH_PAGE_SIZE,
      ),
    );
  }
}

/**
 * Spillested › Saler og spesifikasjon — `/venue/$festivalId/rooms` og `/rooms/$roomId`.
 *
 * Uten `roomId` står den første salen i lista oppe — et svar — så tariffen og basisressursene dens
 * blir stående hos komponenten. Med `roomId` i adressen startes de her.
 */
export function prefetchVenueRooms(
  queryClient: QueryClient,
  me: MeResponse,
  params: VenueRouteParams & { roomId?: string },
  search: RawSearch = {},
): void {
  const organizationId = me.activeOrganizationId;
  const { festivalId, roomId } = params;
  if (organizationId === null || festivalId === '') return;
  const actor = actorFromMe(me);
  if (!allows(actor, roomId === undefined ? 'venue:list' : 'venue:read')) return;

  void queryClient.prefetchQuery(festivalQueryOptions(organizationId, festivalId));
  void queryClient.prefetchQuery(venueRoomsQueryOptions(organizationId, festivalId));
  /* QA15-S B3: fanen som åpnes, er Arrangementer med mindre adressen sier noe annet. */
  const section = validateRoomsSearch(search).section ?? 'events';
  const room = roomId === undefined || roomId === '' ? null : roomId;
  if (section === 'events') prefetchVenueEvents(queryClient, me, params, search, new Date(), room);
  if (section === 'requests') prefetchVenueRequests(queryClient, me, params, search, room);
  if (section === 'spec' && room !== null) {
    void queryClient.prefetchQuery(venueBaseResourcesQueryOptions(organizationId, room));
    if (allows(actor, 'venue_rate:read')) {
      void queryClient.prefetchQuery(venueRentPackagesQueryOptions(organizationId, room));
    }
  }
}

/** Spillested › Datoforespørsler — `/venue/$festivalId/requests`, bak `VenueGate` som Personer. */
export function prefetchVenueRequests(
  queryClient: QueryClient,
  me: MeResponse,
  params: VenueRouteParams,
  search: RawSearch,
  /** Under Saler (QA15-S B3): salen fra stien, eller `null` for alle saler. */
  room?: string | null,
): void {
  const organizationId = me.activeOrganizationId;
  const { festivalId } = params;
  if (organizationId === null || festivalId === '') return;
  if (!allows(actorFromMe(me), 'venue_enquiry:list')) return;

  const validated = validateVenueRequestsSearch(search);
  const rooms =
    room === undefined ? {} : { festivalId, ...(room === null ? {} : { roomId: room }) };
  void queryClient.prefetchQuery(festivalQueryOptions(organizationId, festivalId));
  /* Søkefeltet starter tomt; det står ikke i adressen. */
  void queryClient.prefetchInfiniteQuery(
    venueEnquiriesQueryOptions(organizationId, {
      tab: validated.tab ?? 'waiting',
      search: '',
      ...rooms,
    }),
  );
  void queryClient.prefetchQuery(venueEnquirySummaryQueryOptions(organizationId, rooms));
  void queryClient.prefetchQuery(tablePrefsQueryOptions('venue-requests'));
  if (validated.enquiry !== undefined) {
    void queryClient.prefetchQuery(venueEnquiryQueryOptions(organizationId, validated.enquiry));
    void queryClient.prefetchQuery(
      venueEnquiryMessagesQueryOptions(organizationId, validated.enquiry),
    );
  }
}

/**
 * Control Room › Oversikt — `/control-room`: tallene og SMS-bryteren, begge plattformens. Layoutens
 * `beforeLoad` slipper bare en plattformadmin inn; porten står her også.
 */
export function prefetchControlRoomOverview(queryClient: QueryClient, me: MeResponse): void {
  if (me.isPlatformAdmin !== true) return;
  void queryClient.prefetchQuery(controlRoomOverviewQueryOptions());
  void queryClient.prefetchQuery(controlRoomSmsQueryOptions());
}
